/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { colors, FormControl, HFlow, VFlow } from 'bold-ui'
import { CheckboxField, SwitchField } from 'components/form'
import { resolveValue } from 'components/form/final-form/useField'
import moment from 'cypress/node_modules/moment'
import { useOdontogramasQuery } from 'graphql/hooks.generated'
import { Odontograma, SituacaoCoroaEnum, SituacaoFaceEnum } from 'graphql/types.generated'
import { ParteBucalEnum } from 'graphql/types.generated'
import { useEffect } from 'react'
import { useState } from 'react'
import { useForm, useFormState } from 'react-final-form'
import { MetaPath } from 'util/metaPath'
import { SoapState } from 'view/atendimentos/atendimento-individual/model'

import { FillColor } from '../odontograma/types/Coroa'
import ArcadasField, { ArcadaFieldModel } from './arcadas-field/ArcadasField'
import { CondicoesDenteFieldModel } from './components/CondicoesDenteField'
import { OdontogramaInputs } from './components/OdontogramaInputs'
import { PeriodoOdontogramaSelectModel } from './components/PeriodoOdontogramaSelect/PeriodoOdontogramaSelect'
import { convertOdontogramaQueryModelToOdontogramaFieldModel } from './converter'
import DentesField, { DenteFieldModel } from './dentes-field/DentesField'
import DentesSupranumerariosField, {
  EvolucaoDenteSupranumerarioModel,
} from './dentes-supranumerarios/DentesSupranumerariosField'
import { OdontogramaPosicaoProtese } from './model'
import { OdontogramaView } from './OdontogramaView'
import OutrosField, { OutrosFieldModel } from './outros-field/OutrosField'
import SextantesField, { SextanteFieldModel } from './sextantes-field/SextantesField'
import { getDentePosicaoProtese } from './util'

export type OdontogramaQueryModel = Partial<Odontograma>

type CacheOdontogramaModel = {
  condicoes?: Record<ParteBucalEnum, CondicoesDenteFieldModel>
  alreadySettedAsAusente: {
    superior: boolean
    inferior: boolean
  }
}

export interface OdontogramaFieldModel {
  dentes?: Record<ParteBucalEnum, DenteFieldModel>
  sextantes?: Record<ParteBucalEnum, SextanteFieldModel>
  arcadas?: Record<ParteBucalEnum, ArcadaFieldModel>
  outros?: OutrosFieldModel[]
  dentesSupranumerarios?: EvolucaoDenteSupranumerarioModel[]
  proteseTotalSuperior?: boolean
  proteseTotalInferior?: boolean
  possuiAparelho?: boolean
  possuiContencao?: boolean
  // cache do Odontograma
  cache: CacheOdontogramaModel
}

export interface OdontogramaFieldProps {
  name: MetaPath<OdontogramaFieldModel>
  prontuarioId: ID
  cidadaoDataNascimento: LocalDate
}

export type OdontogramaViewMode = 'dentes' | 'arcadas' | 'sextantes' | 'outros'

interface ViewingOdontogramaParams {
  odontogramaId: ID
  atendimentoId: ID
  dataIniciado: Date
}

const fillColors: FillColor<SituacaoCoroaEnum>[] = [
  {
    id: 1,
    title: 'Não tratado',
    color: colors.red.c40,
    value: SituacaoCoroaEnum.A_SER_TRATADO,
  },
  {
    id: 2,
    title: 'Tratado',
    color: colors.blue.c40,
    value: SituacaoCoroaEnum.TRATADO,
  },
]

export const OdontogramaField = (props: OdontogramaFieldProps) => {
  const { name, prontuarioId, cidadaoDataNascimento } = props
  const [showDeciduos, setShowDeciduos] = useState<boolean>(false)
  const [viewMode, setViewMode] = useState<OdontogramaViewMode>('dentes')
  const [initialValue, setInitialValue] = useState<OdontogramaFieldModel>()
  const [viewingOdontogramaParams, setViewingOdontogramaParams] = useState<ViewingOdontogramaParams>()
  const useMode =
    viewingOdontogramaParams?.odontogramaId && viewingOdontogramaParams?.atendimentoId ? 'viewing' : 'inserting'

  const form = useForm()
  const { values: formValues } = useFormState<SoapState>({ subscription: { values: true } })

  const {
    data: { odontogramas },
  } = useOdontogramasQuery({
    variables: {
      input: {
        prontuarioId: prontuarioId,
        pageParams: {
          size: 1,
        },
      },
    },
  })

  const proteseTotalSuperior = resolveValue<OdontogramaFieldModel['proteseTotalSuperior']>(
    formValues,
    props.name.proteseTotalSuperior
  )
  const proteseTotalInferior = resolveValue<OdontogramaFieldModel['proteseTotalInferior']>(
    formValues,
    props.name.proteseTotalInferior
  )
  const possuiAparelho = resolveValue<OdontogramaFieldModel['possuiAparelho']>(formValues, props.name.possuiAparelho)
  const possuiContencao = resolveValue<OdontogramaFieldModel['possuiContencao']>(formValues, props.name.possuiContencao)
  const dentes = resolveValue<OdontogramaFieldModel['dentes']>(formValues, props.name.dentes)
  const cache = resolveValue<OdontogramaFieldModel['cache']>(formValues, props.name.cache)

  const toggleViewMode = (value: string) => {
    setViewMode(value as OdontogramaViewMode)
  }

  const toggleDeciduos = () => {
    setShowDeciduos(!showDeciduos)
  }

  const updateCache = (values: CacheOdontogramaModel) => {
    form.change(name.cache.absolutePath(), values)
  }

  const getDenteCondicoesByProtese = (posicaoProtese: OdontogramaPosicaoProtese) => {
    return Object.fromEntries(
      Object.entries(dentes)
        ?.map(([key, value]) => [key, value.condicoesDente])
        .filter(([key]: [ParteBucalEnum]) => getDentePosicaoProtese(key) === posicaoProtese)
    ) as Record<ParteBucalEnum, CondicoesDenteFieldModel>
  }

  const changeCondicoesDentesByPosicaoProtese = (
    posicaoProtese: OdontogramaPosicaoProtese,
    newValue?: CondicoesDenteFieldModel,
    funcValue?: (k: ParteBucalEnum) => any
  ) => {
    form.change(
      name.dentes.absolutePath(),
      Object.fromEntries(
        Object.entries(dentes).map(([key, value]: [ParteBucalEnum, DenteFieldModel]) => [
          key,
          {
            ...value,
            condicoesDente:
              getDentePosicaoProtese(key) === posicaoProtese
                ? newValue || (funcValue && funcValue(key))
                : value.condicoesDente,
          },
        ])
      )
    )
  }

  const clearDenteCondicoesByProtese = (posicaoProtese: OdontogramaPosicaoProtese) => {
    changeCondicoesDentesByPosicaoProtese(posicaoProtese, null)
  }

  const populateDentesCondicoesFromCache = (posicaoProtese: OdontogramaPosicaoProtese) => {
    cache?.condicoes && changeCondicoesDentesByPosicaoProtese(posicaoProtese, null, (k) => cache.condicoes[k])
  }

  const setDentesAsAusente = (posicaoProtese: OdontogramaPosicaoProtese) => {
    changeCondicoesDentesByPosicaoProtese(posicaoProtese, [SituacaoFaceEnum.AUSENTE])
    setAlreadySettedAsAusente(posicaoProtese)
  }

  const setAlreadySettedAsAusente = (posicaoProtese: OdontogramaPosicaoProtese) => {
    if (posicaoProtese === 'superior')
      updateCache({
        ...cache,
        alreadySettedAsAusente: { superior: true, inferior: cache?.alreadySettedAsAusente?.inferior },
      })

    if (posicaoProtese === 'inferior')
      updateCache({
        ...cache,
        alreadySettedAsAusente: { inferior: true, superior: cache?.alreadySettedAsAusente?.superior },
      })
  }

  const onChangePeriodo = (periodo: PeriodoOdontogramaSelectModel) => {
    if (!periodo || periodo.odontogramaId === null) {
      setViewingOdontogramaParams(undefined)
    } else {
      setViewingOdontogramaParams({
        odontogramaId: periodo.odontogramaId,
        atendimentoId: periodo.atendimentoId,
        dataIniciado: periodo.dataIniciado,
      })
    }
  }

  const onChangeProtese = (posicaoProtese: OdontogramaPosicaoProtese, possuiProtese: boolean) => {
    const condicoesDentes = getDenteCondicoesByProtese(posicaoProtese)

    if (!possuiProtese) {
      updateCache({
        ...cache,
        condicoes: { ...cache?.condicoes, ...condicoesDentes },
      })

      clearDenteCondicoesByProtese(posicaoProtese)
    } else {
      if (posicaoProtese === 'superior') {
        if (initialValue?.proteseTotalSuperior && !cache?.alreadySettedAsAusente?.superior) {
          setDentesAsAusente('superior')
        } else {
          populateDentesCondicoesFromCache(posicaoProtese)
        }
      }

      if (posicaoProtese === 'inferior') {
        if (initialValue?.proteseTotalInferior && !cache?.alreadySettedAsAusente?.inferior) {
          setDentesAsAusente('inferior')
        } else {
          populateDentesCondicoesFromCache(posicaoProtese)
        }
      }
    }
  }

  useEffect(() => {
    const odontograma = odontogramas?.content[0]

    odontograma && setInitialValue(convertOdontogramaQueryModelToOdontogramaFieldModel(odontograma, fillColors))
  }, [odontogramas])

  useEffect(() => {
    const limitYear = 16

    if (useMode === 'inserting') {
      const limitDate = moment().subtract(limitYear, 'y')

      setShowDeciduos(moment(cidadaoDataNascimento).isAfter(limitDate))
    } else if (useMode === 'viewing') {
      const limitDate =
        viewingOdontogramaParams?.dataIniciado && moment(viewingOdontogramaParams.dataIniciado).subtract(limitYear, 'y')

      limitDate && setShowDeciduos(moment(cidadaoDataNascimento).isAfter(limitDate, 'day'))
    }
  }, [cidadaoDataNascimento, useMode, viewingOdontogramaParams])

  return (
    <VFlow>
      <OdontogramaInputs
        prontuarioId={prontuarioId}
        viewMode={viewMode}
        showDeciduos={showDeciduos}
        toggleViewMode={toggleViewMode}
        toggleShowDeciduos={toggleDeciduos}
        onChangePeriodo={onChangePeriodo}
      />

      {useMode === 'inserting' ? (
        <VFlow>
          <HFlow
            alignItems='center'
            hSpacing={2}
            style={css`
              padding: 0 0.5rem;
            `}
          >
            <FormControl
              label={
                <span
                  css={css`
                    white-space: nowrap;
                  `}
                >
                  Possui aparelho
                </span>
              }
            >
              <SwitchField
                name={name.possuiAparelho}
                label={possuiAparelho ? 'Sim' : 'Não'}
                style={{ marginTop: '0.25rem' }}
                initialValue={initialValue?.possuiAparelho}
              />
            </FormControl>

            <FormControl label='Possui contenção'>
              <SwitchField
                name={name.possuiContencao}
                label={possuiContencao ? 'Sim' : 'Não'}
                style={{ marginTop: '0.25rem' }}
                initialValue={initialValue?.possuiContencao}
              />
            </FormControl>

            <FormControl label='Prótese'>
              <HFlow
                style={css`
                  margin-left: -0.25rem;
                `}
              >
                <CheckboxField
                  name={name.proteseTotalSuperior}
                  label='Total superior'
                  style={css`
                    white-space: nowrap;
                    margin-top: 0.25rem;
                  `}
                  initialValue={initialValue?.proteseTotalSuperior}
                  onChange={() => onChangeProtese('superior', proteseTotalSuperior)}
                />
                <CheckboxField
                  name={name.proteseTotalInferior}
                  label='Total inferior'
                  style={css`
                    white-space: nowrap;
                    margin-top: 0.25rem;
                  `}
                  initialValue={initialValue?.proteseTotalInferior}
                  onChange={() => onChangeProtese('inferior', proteseTotalInferior)}
                />
              </HFlow>
            </FormControl>
          </HFlow>

          {viewMode === 'dentes' && (
            <VFlow>
              <DentesField
                name={name.dentes}
                cidadaoDataNascimento={cidadaoDataNascimento}
                showDeciduos={showDeciduos}
                fillColors={fillColors}
                proteseTotalSuperior={proteseTotalSuperior}
                proteseTotalInferior={proteseTotalInferior}
                initialValue={initialValue?.dentes}
              />

              <DentesSupranumerariosField
                name={name.dentesSupranumerarios}
                cidadaoDataNascimento={cidadaoDataNascimento}
              />
            </VFlow>
          )}

          {viewMode === 'arcadas' && (
            <ArcadasField
              name={name.arcadas}
              cidadaoDataNascimento={cidadaoDataNascimento}
              proteseTotalSuperior={proteseTotalSuperior}
              proteseTotalInferior={proteseTotalInferior}
            />
          )}

          {viewMode === 'sextantes' && (
            <SextantesField
              name={name.sextantes}
              cidadaoDataNascimento={cidadaoDataNascimento}
              proteseTotalSuperior={proteseTotalSuperior}
              proteseTotalInferior={proteseTotalInferior}
            />
          )}

          {viewMode === 'outros' && <OutrosField name={name.outros} cidadaoDataNascimento={cidadaoDataNascimento} />}
        </VFlow>
      ) : (
        viewingOdontogramaParams && (
          <OdontogramaView
            odontogramaId={viewingOdontogramaParams.odontogramaId}
            atendimentoId={viewingOdontogramaParams.atendimentoId}
            prontuarioId={prontuarioId}
            showDeciduos={showDeciduos}
            fillColors={fillColors}
            viewMode={viewMode}
          />
        )
      )}
    </VFlow>
  )
}
